import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import StepComponent from "./StepComponent";
import ToolCard from "../../../common/element/Card";
import Button from "../../../common/element/Buttons";
import Sidebar from "./sideBar";
import ThreeDotLoader from "../../../common/element/ButtonLoader/ThreeDotLoader";
import SweetAlertService from "../../../common/SweetAlert/SweetAlert";
import ExpiredTokenPage from "../ExpiredTokenPage";
import LoginFormFORprospect from "../LoginFormFORprospect";
import { submitSessionUrl } from "../../../redux/SessionUrl/sessionUrlSlice";

// Redux Actions
import {
  fetchNcodes,
  fetchResult,
  checkTokenExpiry,
  reorderBaselines,
} from "../../../redux/tool/toolSlice";

// Redux Selectors
import {
  selectNcodesData,
  selectNcodesLoading,
  selectNcodesError,
  selectResultLoading,
  selectResultError,
  selectTokenExpiryCheckData,
  selectTokenExpiryCheckLoading,
  selectReorderBaselinesLoading,
  selectReorderBaselinesError,
} from "../../../redux/tool/toolSelectors";
import ThreeDotBouncingLoader from "../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import InvalidTokenPage from "../InvalidToken";

const TOTAL_STEPS = 9;

const emptySelections = () =>
  Object.fromEntries(Array.from({ length: TOTAL_STEPS }, (_, i) => [i + 1, []]));

const BaseLine = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.removeItem('prospect_token');
    } catch (e) { }
  }, []);

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const companyIdParam = searchParams.get("company_id");
  const companyNameParam = searchParams.get("company_name");
  const companySessionIdParam = searchParams.get("company_session_id");
  const sessionNameParam = searchParams.get("session_name");
  const sessionTypeParam = searchParams.get("session_type");
  const isSessionUrlFlow = sessionTypeParam === "session_url";
  const isInterviewCandidate = searchParams.get("is_interview_candidate") === "true";
  const isProspect = searchParams.get('is_prospect') === 'true';
  const [prospectRequired] = useState(
    Boolean(companyIdParam && companyNameParam),
  );
  const [prospectConfirmed, setProspectConfirmed] = useState(false);
  const storedProspectToken = (() => { try { return localStorage.getItem('prospect_token'); } catch (e) { return null; } })();
  const [prospectToken, setProspectToken] = useState(storedProspectToken || token || null);
  const [sessionUrlUserData, setSessionUrlUserData] = useState(null);

  const ncodesData = useSelector(selectNcodesData);
  const ncodesLoading = useSelector(selectNcodesLoading);
  const ncodesError = useSelector(selectNcodesError);

  const tokenExpiryData = useSelector(selectTokenExpiryCheckData);
  const tokenExpiryLoading = useSelector(selectTokenExpiryCheckLoading);

  const resultLoading = useSelector(selectResultLoading);
  const resultError = useSelector(selectResultError);

  const reorderLoading = useSelector(selectReorderBaselinesLoading);
  const reorderError = useSelector(selectReorderBaselinesError);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepSelections, setStepSelections] = useState(emptySelections);

  // Step 10 state
  const [showStep10, setShowStep10] = useState(false);
  const [rankedBaselines, setRankedBaselines] = useState([]);
  const [reorderedBaselines, setReorderedBaselines] = useState([]);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [step10MobileMenuOpen, setStep10MobileMenuOpen] = useState(false);

  const stepConfigs = {
    1: {
      title: "Step 1",
      instruction:
        "From the list below, select as many baselines as you feel are important to you. A minimum of 50 selections is required.",
      requiredCount: 50,
    },
    2: {
      title: "Step 2",
      instruction:
        "Now from the baselines you have picked, select 24 that are important to you.",
      requiredCount: 24,
    },
    3: {
      title: "Step 3",
      instruction:
        "Now from the baselines you have picked, select 12 that are important to you.",
      requiredCount: 12,
    },
    4: {
      title: "Step 4",
      instruction:
        "Now from the baselines you have picked, select 6 that are important to you.",
      requiredCount: 6,
    },
    5: {
      title: "Step 5",
      instruction:
        "Your top 6 baselines are shown below. Please select 5 baselines that are very important to you and sacrifice 1 baseline.",
      requiredCount: 5,
    },
    6: {
      title: "Step 6",
      instruction:
        "Please select 4 baselines from the below options that are very important to you and sacrifice 1 baseline.",
      requiredCount: 4,
    },
    7: {
      title: "Step 7",
      instruction:
        "Please select 3 baselines from the below options that are very important to you and sacrifice 1 baseline.",
      requiredCount: 3,
    },
    8: {
      title: "Step 8",
      instruction:
        "Please select 2 baselines from the below options that are very important to you and sacrifice 1 baseline.",
      requiredCount: 2,
    },
    9: {
      title: "Step 9",
      instruction:
        "Please select only 1 baseline from the below options that is the most important to you.",
      requiredCount: 1,
    },
  };

  useEffect(() => {
    if (token) {
      dispatch(checkTokenExpiry(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (!prospectRequired || prospectConfirmed) {
      dispatch(fetchNcodes("baseline"));
    }
  }, [dispatch, prospectRequired, prospectConfirmed]);

  const masterNCodes = useMemo(() => {
    if (!Array.isArray(ncodesData)) return [];
    return ncodesData.map((item) => ({
      id: item.id,
      text: item.question || item.ncode || item.text,
      description: item.description || item.detail || null,
    }));
  }, [ncodesData]);

  const handleGoHome = () => {
    setCurrentStep(1);
    setCompletedSteps([]);
    setStepSelections(emptySelections());
    setShowStep10(false);
    setRankedBaselines([]);
    setReorderedBaselines([]);
    setShowCongratulations(false);

    if (isSessionUrlFlow) {
      setProspectConfirmed(false);
      setSessionUrlUserData(null);
      return;
    }

    if (isProspect) {
      navigate("/");
      return;
    }

    navigate({ pathname: "/baselines", search: searchParams.toString() });
    if (token) dispatch(checkTokenExpiry(token));
  };

  const handleSelectionChange = (newSelection) => {
    setStepSelections((prev) => {
      const updated = { ...prev, [currentStep]: newSelection };
      for (let s = currentStep + 1; s <= TOTAL_STEPS; s++) {
        updated[s] = [];
      }
      return updated;
    });
  };

  const handleNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Compute ranked baselines from elimination logic
      const getLeftOut = (fromStep, toStep) => {
        const selectedInNext = new Set(stepSelections[toStep].map((i) => i.id));
        return stepSelections[fromStep].filter((i) => !selectedInNext.has(i.id));
      };

      // Rank 1: selected in step 9 | Rank 2: left in step 9 | ... | Rank 6: left in step 5
      const ranked = [
        ...stepSelections[9],    // Rank 1
        ...getLeftOut(8, 9),     // Rank 2
        ...getLeftOut(7, 8),     // Rank 3
        ...getLeftOut(6, 7),     // Rank 4
        ...getLeftOut(5, 6),     // Rank 5
        ...getLeftOut(4, 5),     // Rank 6
      ];

      const selectedIds = ranked.map((item) => item.id);

      if (!isSessionUrlFlow) {
        const effectiveToken = prospectToken || token;
        dispatch(fetchResult({ ids: selectedIds, token: effectiveToken, id: "baseline" }));
      }

      setRankedBaselines(ranked);
      setReorderedBaselines(ranked);

      if (isInterviewCandidate || isProspect) {
        // Skip Step 10 and reorder API — show result directly
        setShowCongratulations(true);
        return;
      }

      setShowStep10(true);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(reorderedBaselines);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setReorderedBaselines(items);
  };

  const handleReorderSubmit = async () => {
    const userOrderedItems = reorderedBaselines.map((item) => ({
      id: item.id,
      text: item.text,
    }));

    if (isSessionUrlFlow) {
      const resultAction = await dispatch(
        submitSessionUrl({
          company_session_id: Number(companySessionIdParam),
          name: sessionUrlUserData?.name || "",
          email: sessionUrlUserData?.email || "",
          phone: sessionUrlUserData?.phone || "",
          result_json: {
            ids: rankedBaselines.map((item) => item.id),
            user_ordered_items: userOrderedItems,
          },
        })
      );
      if (submitSessionUrl.rejected.match(resultAction)) {
        SweetAlertService.showError({
          title: "Error!",
          message: resultAction.payload || "Something went wrong. Please try again.",
        });
        return;
      }
      if (submitSessionUrl.fulfilled.match(resultAction)) {
        SweetAlertService.showSuccess({
          title: "Success!",
          message: "Test has been submitted successfully.",
          onConfirm: () => { setShowCongratulations(true); },
        });
      }
      return;
    }

    const effectiveToken = prospectToken || token;
    if (!effectiveToken) {
      SweetAlertService.showError({
        title: "Invalid Link",
        message: "Token is missing. Please use a valid link.",
      });
      return;
    }

    const resultAction = await dispatch(
      reorderBaselines({
        payload: { user_ordered_items: userOrderedItems },
        token: effectiveToken,
      })
    );

    if (reorderBaselines.rejected.match(resultAction)) {
      SweetAlertService.showError({
        title: "Error!",
        message: resultAction.payload || "Something went wrong. Please try again.",
      });
      return;
    }

    setShowCongratulations(true);
  };

  const handleCancel = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setCompletedSteps((prev) => prev.filter((s) => s <= newStep));
      return;
    }

    SweetAlertService.showConfirmation({
      title: "Are you sure?",
      message: "This will reset all your progress.",
      confirmButtonText: "Yes, Reset",
      cancelButtonText: "Cancel",
      onConfirm: () => {
        setCurrentStep(1);
        setCompletedSteps([]);
        setStepSelections(emptySelections());
      },
    });
  };

  const currentConfig = stepConfigs[currentStep];

  const availableCodes =
    currentStep === 1
      ? masterNCodes
      : stepSelections[currentStep - 1] || [];

  const selectedCodes = stepSelections[currentStep];

  // ============================================
  // CONDITIONAL RENDER LOGIC
  // ============================================

  if (tokenExpiryLoading) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <ThreeDotBouncingLoader />
        </div>
      </div>
    );
  }

  if (
    tokenExpiryData?.token_valid == false &&
    tokenExpiryData?.expired == false
  ) {
    return <InvalidTokenPage />;
  }

  if (tokenExpiryData?.expired == true) {
    return <ExpiredTokenPage />;
  }

  if (prospectRequired && !prospectConfirmed) {
    const headerOverrides = {
      title: "KNOW YOUR BASELINES",
      subtitle: "Enter your details to access the tool",
      highlighted: "",
    };

    const initialValues = {
      company_id: Number(companyIdParam) || 0,
      company_name: companyNameParam || "",
    };

    return (
      <LoginFormFORprospect
        headerOverrides={headerOverrides}
        initialValues={initialValues}
        sessionType={sessionTypeParam}
        sessionName={sessionNameParam}
        companySessionId={companySessionIdParam}
        onSuccess={(payload) => {
          if (isSessionUrlFlow && payload?.userData) {
            setSessionUrlUserData(payload.userData);
          }
          setProspectConfirmed(true);
          const returnedToken = payload?.token || payload?.data?.token || null;
          if (returnedToken) setProspectToken(returnedToken);
          dispatch(fetchNcodes("baseline"));
        }}
      />
    );
  }

  if (ncodesLoading) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Baselines...</p>
        </div>
      </div>
    );
  }

  if (ncodesError) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading Baselines: {ncodesError}
          </p>
          <button
            onClick={() => dispatch(fetchNcodes("baseline"))}
            className="px-4 py-2 bg-[#FF8E1C] text-white rounded hover:bg-[#ff7a00]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Congratulations screen — shown after successful reorder submit
  if (showCongratulations) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-xl w-full">
          <h1 className="font-lato font-bold text-[36px] text-[#FF8E1C] mb-4">
            Congratulations!
          </h1>
          <p className="font-lato text-[16px] text-black mb-6">
            Your top {reorderedBaselines.length} baselines in the order of importance are as below:
          </p>
          <div className="grid grid-cols-3 gap-3 mb-8">
            {reorderedBaselines.map((item, index) => (
              <div
                key={item.id || index}
                className="bg-[#FF8E1C] text-white rounded-lg py-3 px-4 font-lato font-semibold text-[14px] text-center"
              >
                {index + 1}. {item.text}
              </div>
            ))}
          </div>
          <button
            onClick={handleGoHome}
            className="px-8 py-3 border border-[#FF8E1C] text-[#FF8E1C] rounded-lg font-lato font-semibold text-[16px] hover:bg-[#FFF9F3] transition-colors"
          >
            Visit Home Page
          </button>
        </div>
      </div>
    );
  }

  // Step 10 — result review + drag-and-drop reordering
  if (showStep10) {
    return (
      <div className="w-full min-h-screen bg-white p-4">
        <ToolCard>
          <div className="rounded-lg p-3 md:p-6 pt-6 md:pt-8 text-start">

            {/* Mobile header */}
            <div className="flex justify-between items-center md:hidden mb-4">
              <span className="font-lato font-semibold text-[14px] text-black">
                Step 10 of 10
              </span>
              <button
                className={`mobile-menu ${step10MobileMenuOpen ? 'active' : ''}`}
                onClick={() => setStep10MobileMenuOpen(true)}
                aria-label="Toggle steps menu"
              >
                <svg width="24" height="19" viewBox="0 0 32 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.52381C0 0.682225 0.68224 0 1.52381 0H30.4762C31.3178 0 32 0.682225 32 1.52381C32 2.36539 31.3178 3.04762 30.4762 3.04762H1.52381C0.68224 3.04762 0 2.36539 0 1.52381Z" fill="black"/>
                  <path d="M0 12.1902C0 11.3486 0.68224 10.6662 1.52381 10.6662L18.2857 10.6667C19.1273 10.6667 19.8095 11.3489 19.8095 12.1905C19.8095 13.0321 19.1273 13.7143 18.2857 13.7143L1.52381 13.714C0.68224 13.714 0 13.0316 0 12.1902Z" fill="black"/>
                  <path d="M1.52381 21.333C0.68224 21.333 0 22.0152 0 22.8568C0 23.6983 0.68224 24.3805 1.52381 24.3805H27.4286C28.2702 24.3805 28.9524 23.6983 28.9524 22.8568C28.9524 22.0152 28.2702 21.333 27.4286 21.333H1.52381Z" fill="black"/>
                </svg>
              </button>
            </div>

            {/* Step progress bar — steps 1-9 completed, step 10 active */}
            <Sidebar
              totalSteps={10}
              currentStep={10}
              completedSteps={completedSteps}
              isMobileOpen={step10MobileMenuOpen}
              onClose={() => setStep10MobileMenuOpen(false)}
            />

            <h2 className="font-lato font-bold text-[15px] md:text-[20px] lg:text-[24px] leading-[130%] md:leading-[115%] text-black text-center mb-4 md:mb-8">
              Review and confirm your top 6 baselines in the order of importance.
            </h2>

            {/* Loader — inside card, steps remain visible above */}
            {resultLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8E1C] mx-auto mb-4"></div>
                  <p className="text-gray-600 font-lato text-[14px]">Processing your results...</p>
                </div>
              </div>
            ) : resultError ? (
              <div className="text-center py-16">
                <p className="text-red-600 mb-4 font-lato text-[14px]">Error processing results. Please try again.</p>
                <Button
                  onClick={handleGoHome}
                  className="px-6 py-3 bg-[#FF8E1C] text-white rounded-lg font-lato font-semibold hover:bg-[#E67E0C]"
                >
                  Go Home
                </Button>
              </div>
            ) : (
              <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">

              {/* Left — Result To Be Saved */}
              <div>
                <h3 className="font-lato font-semibold text-[15px] md:text-[18px] lg:text-[20px] leading-[115%] text-black mb-3 md:mb-4">
                  Result To Be Saved
                </h3>
                <p className="font-lato text-[13px] md:text-[14px] text-gray-500 mb-4">
                  As per assessment, your top 6 baselines in the order of importance are as below:
                </p>
                <div className="space-y-2 md:space-y-3 max-h-[45vh] overflow-y-auto">
                  {rankedBaselines.map((item, index) => (
                    <div
                      key={item.id}
                      className="text-center font-semibold text-[12px] md:text-[14px] font-lato p-2 bg-[#FF8E1C] text-white rounded-lg"
                    >
                      {index + 1}. {item.text}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — Drag-and-drop reordering */}
              <div>
                <h3 className="font-lato font-semibold text-[15px] md:text-[18px] lg:text-[20px] leading-[115%] text-black mb-3 md:mb-4">
                  After giving it a final thought
                </h3>
                <p className="font-lato text-[13px] md:text-[14px] text-gray-500 mb-4">
                  Your final top 6 baselines in the order of importance are as below. Drag to reorder.
                </p>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="baseline-reorder">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-2 md:space-y-3 max-h-[45vh] overflow-y-auto"
                      >
                        {reorderedBaselines.map((item, index) => (
                          <Draggable
                            key={item.id.toString()}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center gap-2 font-semibold text-[12px] md:text-[14px] font-lato p-2 bg-[#FF8E1C] text-white rounded-lg select-none${snapshot.isDragging ? " opacity-80 shadow-xl" : ""}`}
                              >
                                <span
                                  {...provided.dragHandleProps}
                                  className="cursor-grab text-white text-lg shrink-0 px-1"
                                >
                                  ⠿
                                </span>
                                <span>{index + 1}. {item.text}</span>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>

            </div>

            {reorderError && (
              <div className="mt-4">
                <p className="text-red-600 font-lato text-[13px] md:text-[14px]">{reorderError}</p>
              </div>
            )}

            {/* Action buttons — same style as StepComponent */}
            <div className="flex justify-start md:justify-end space-x-3 md:space-x-4 mt-5 md:mt-8">
              <Button
                onClick={() => setShowStep10(false)}
                className="bg-[#FFF4E5] text-black px-[14px] md:px-[18px] py-3 md:py-4 w-[120px] md:w-[150px] h-11 md:h-14 rounded-[12px] font-lato font-bold text-[14px] md:text-[16px] leading-[150%]"
              >
                Back
              </Button>
              <Button
                onClick={handleReorderSubmit}
                disabled={reorderLoading}
                className={`px-[14px] md:px-[18px] py-3 md:py-4 w-[120px] md:w-[150px] h-11 md:h-14 rounded-[12px] font-lato font-bold text-[14px] md:text-[16px] leading-[150%] transition-all duration-200 ${
                  !reorderLoading
                    ? 'bg-[#FF8E1C] text-white hover:bg-[#E67E0C]'
                    : 'bg-[#FF8E1C] text-white opacity-80 cursor-not-allowed'
                }`}
              >
                {reorderLoading ? <ThreeDotLoader /> : 'Submit'}
              </Button>
            </div>
            </>
            )}

          </div>
        </ToolCard>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white p-4">
      <StepComponent
        stepNumber={currentStep}
        completedSteps={completedSteps}
        title={currentConfig.title}
        instruction={currentConfig.instruction}
        requiredCount={currentConfig.requiredCount}
        availableCodes={availableCodes}
        selectedCodes={selectedCodes}
        onSelectionChange={handleSelectionChange}
        onNext={handleNext}
        onCancel={handleCancel}
        isLastStep={false}
        isSubmitting={false}
        totalSteps={isInterviewCandidate || isProspect ? 9 : 10}
      />
    </div>
  );
};

export default BaseLine;
